<template>
  <div class="submission-page">
    <Head :title="pageTitle" />

    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.14em] text-brand-500">Operations</p>
        <h1 class="mt-1 text-3xl font-bold tracking-tight text-brand-900">{{ pageTitle }}</h1>
        <p class="mt-1.5 text-sm text-gray-500">{{ pageSubtitle }}</p>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50"
          @click="reset"
        >
          Reset
        </button>
        <button
          type="button"
          class="inline-flex items-center rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700"
          @click="downloadExcel"
        >
          Download Excel
        </button>
        <button
          type="button"
          class="inline-flex items-center rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
          @click="applyFilters"
        >
          Apply filters
        </button>
      </div>
    </div>

    <div class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Results</p>
        <p class="mt-1 text-xl font-bold text-brand-900">{{ formatNumber(organizations.total || 0) }}</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Page</p>
        <p class="mt-1 text-xl font-bold text-brand-900">
          {{ organizations.current_page || 1 }}
          <span class="text-sm font-medium text-gray-400">/ {{ organizations.last_page || 1 }}</span>
        </p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-gradient-to-br from-brand-600 to-brand-800 px-4 py-3 text-white shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-white/70">Source</p>
        <p class="mt-1 text-lg font-bold">{{ isApp ? 'Mobile App' : 'Landing Web' }}</p>
      </div>
    </div>

    <div class="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div class="mb-4">
        <h2 class="text-sm font-bold text-brand-900">Filters</h2>
        <p class="text-xs text-gray-500">Filter by period, channel, keyword, and page size</p>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Start date</label>
          <input
            v-model="form.datestart"
            type="date"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">End date</label>
          <input
            v-model="form.dateend"
            type="date"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Search</label>
          <input
            v-model="form.sourcename"
            type="text"
            placeholder="Name, phone, ID, email..."
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
            @keyup.enter="applyFilters"
          />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Channel</label>
          <select
            v-model="form.channel"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="">All channels</option>
            <option v-for="option in options" :key="option.value" :value="option.value">
              {{ option.text }}
            </option>
          </select>
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Per page</label>
          <select
            v-model="form.paging"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option :value="5">5</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
      </div>
    </div>

    <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div class="border-b border-gray-100 px-5 py-3">
        <h2 class="text-sm font-bold text-brand-900">Submission records</h2>
        <p class="text-xs text-gray-500">
          Showing {{ organizations.data.length }} of {{ formatNumber(organizations.total || 0) }}
        </p>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full">
          <thead>
            <tr class="border-b border-gray-100 bg-brand-50/60 text-left">
              <th
                v-for="column in tableColumns"
                :key="column.key"
                class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600"
              >
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in organizations.data"
              :key="row.id || index"
              class="border-b border-gray-50 transition-colors hover:bg-brand-50/40"
            >
              <td
                v-for="column in tableColumns"
                :key="column.key"
                class="whitespace-nowrap px-4 py-3.5 align-top text-sm text-gray-700"
              >
                <template v-if="column.key === 'no'">
                  <span class="font-semibold text-gray-500">{{ calculateIncrement(index) }}</span>
                </template>
                <template v-else-if="column.key === 'result'">
                  <span
                    class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold"
                    :class="resultBadgeClass(row.result)"
                  >
                    {{ displayValue(row.result) }}
                  </span>
                </template>
                <template v-else-if="column.key === 'rqusAmt' || column.key === 'monthInc'">
                  <span class="font-semibold text-brand-800">{{ formatMoney(row[column.key]) }}</span>
                </template>
                <template v-else-if="column.key === 'created_at' || column.key === 'updated_at'">
                  {{ formatDate(row[column.key]) }}
                </template>
                <template v-else>
                  {{ displayValue(row[column.key]) }}
                </template>
              </td>
            </tr>

            <tr v-if="!organizations.data.length">
              <td :colspan="tableColumns.length" class="px-4 py-16 text-center">
                <p class="text-base font-bold text-brand-900">No submissions found</p>
                <p class="mt-1 text-sm text-gray-500">Try adjusting the date range, channel, or search keyword.</p>
                <button
                  type="button"
                  class="mt-4 rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
                  @click="reset"
                >
                  Clear filters
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="border-t border-gray-100 bg-gray-50 px-5 py-3">
        <pagination class="mt-0" :links="organizations.links || []" preserve-scroll />
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import moment from 'moment'

export default {
  components: {
    Head,
    Icon,
    Pagination,
  },
  layout: Layout,

  props: {
    filters: Object,
    optionsData: Array,
    organizations: Object,
    datangdari: String,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        trashed: this.filters.trashed,
        selected: this.filters.pilihan,
        channel: this.filters.channel,
        paging: this.filters.paging || 5,
        datestart: this.filters.datestart,
        dateend: this.filters.dateend,
        sourcename: this.filters.sourcename,
        datangdari: this.datangdari,
      },
      tableColumns: [
        { key: 'no', label: 'No' },
        { key: 'appc_no', label: 'App No' },
        { key: 'result', label: 'Result' },
        { key: 'custNm', label: 'Customer' },
        { key: 'eKtpNum', label: 'NIK' },
        { key: 'mbphNo', label: 'Phone' },
        { key: 'emadr', label: 'Email' },
        { key: 'dob', label: 'DOB' },
        { key: 'lonUseCd', label: 'Loan Use' },
        { key: 'rqusAmt', label: 'Request Amt' },
        { key: 'lonTr', label: 'Tenor' },
        { key: 'crcdCnt', label: 'CC Count' },
        { key: 'area', label: 'Area' },
        { key: 'jobGbCd', label: 'Job Code' },
        { key: 'jobPlace', label: 'Workplace' },
        { key: 'workStMm', label: 'Work MM' },
        { key: 'workStYyyy', label: 'Work YYYY' },
        { key: 'monthInc', label: 'Monthly Inc' },
        { key: 'lonEmpSts', label: 'Emp Status' },
        { key: 'sector', label: 'Sector' },
        { key: 'agtCd', label: 'Agent' },
        { key: 'path1', label: 'Path' },
        { key: 'memo', label: 'Memo' },
        { key: 'ktaOs', label: 'KTA OS' },
        { key: 'global_id', label: 'Global ID' },
        { key: 'result_at', label: 'Result At' },
        { key: 'agrmYn', label: 'Agreement' },
        { key: 'reject_detail', label: 'Reject Detail' },
        { key: 'reject_reason', label: 'Reject Reason' },
        { key: 'response', label: 'Response' },
        { key: 'updated_at', label: 'Updated' },
        { key: 'created_at', label: 'Created' },
      ],
    }
  },
  computed: {
    isApp() {
      return String(this.datangdari || '').toLowerCase().includes('mobile')
    },
    pageTitle() {
      return this.isApp ? 'Submission (App)' : 'Submission (Web)'
    },
    pageSubtitle() {
      return this.isApp
        ? 'Review and export mobile app loan submissions.'
        : 'Review and export web landing loan submissions.'
    },
    options() {
      if (Array.isArray(this.optionsData)) {
        return this.optionsData
          .map((option) => {
            if (option && typeof option === 'object' && 'agtCd' in option && 'name' in option) {
              return {
                value: option.agtCd,
                text: option.name,
              }
            }
            return null
          })
          .filter((option) => option !== null)
      }
      return []
    },
  },
  methods: {
    applyFilters() {
      this.$inertia.get(
        '/submission' + this.form.datangdari,
        pickBy(this.form),
        {
          preserveState: true,
          preserveScroll: true,
        },
      )
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('id-ID')
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '-'
      const num = Number(value)
      if (Number.isNaN(num)) return value
      return num.toLocaleString('id-ID', { style: 'currency', currency: 'IDR' }).replace(',00', '')
    },
    formatDate(date) {
      if (!date) return '-'
      return moment.utc(date).format('DD/MM/YY, H:mm:ss')
    },
    displayValue(value) {
      return value === null || value === undefined || value === '' ? '-' : value
    },
    resultBadgeClass(result) {
      const value = String(result || '').toLowerCase()
      if (value.includes('approve') || value.includes('success') || value === 'y') {
        return 'bg-teal-50 text-teal-700'
      }
      if (value.includes('reject') || value.includes('fail') || value === 'n') {
        return 'bg-red-50 text-red-700'
      }
      if (value.includes('pending') || value.includes('process')) {
        return 'bg-amber-50 text-amber-700'
      }
      return 'bg-gray-100 text-gray-600'
    },
    downloadExcel() {
      const queryParams = new URLSearchParams(this.form).toString()
      window.location = `/submission/download-excel?${queryParams}`
    },
    calculateIncrement(index) {
      const currentPage = this.organizations.current_page
      const pageSize = this.organizations.per_page
      return (currentPage - 1) * pageSize + index + 1
    },
    reset() {
      this.form = mapValues(this.form, () => null)
      this.form.paging = 5
      this.form.datangdari = this.datangdari
      this.applyFilters()
    },
  },
}
</script>
